import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { updateUserTheme } from "../service/apiService";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  /** User's selected mode. May be "system" (follow OS). */
  theme: ThemeMode;
  /** Concrete theme actually applied right now. Always light or dark. */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemeMode) => void;
  /** Apply a hint from the host environment (e.g. Microsoft Teams). No-op if the user has already chosen explicitly. */
  applyHostHint: (resolved: ResolvedTheme) => void;
  /** Apply the persisted preference loaded from the server. Marks the user as having chosen explicitly. */
  hydrateFromServer: (theme: ThemeMode) => void;
}

export const THEME_STORAGE_KEY = "kyva.theme";

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const isThemeMode = (v: unknown): v is ThemeMode =>
  v === "light" || v === "dark" || v === "system";

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const readStoredTheme = (): ThemeMode | null => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(stored) ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredTheme = (next: ThemeMode) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    /* noop — storage may be unavailable in private mode / iframes */
  }
};

const applyThemeClass = (resolved: ResolvedTheme) => {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  root.style.colorScheme = resolved;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(
    () => readStoredTheme() ?? "system"
  );
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  // Track whether the user has ever explicitly set a theme. Once true,
  // applyHostHint is ignored — the user's choice wins.
  const userHasChosenRef = useRef<boolean>(readStoredTheme() !== null);

  // OS preference change listener.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) =>
      setSystemTheme(e.matches ? "dark" : "light");

    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  // Cross-tab sync: when another tab writes to localStorage, mirror it here.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== THEME_STORAGE_KEY) return;
      if (e.newValue === null) {
        userHasChosenRef.current = false;
        setThemeState("system");
        return;
      }
      if (isThemeMode(e.newValue)) {
        userHasChosenRef.current = true;
        setThemeState(e.newValue);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const resolvedTheme: ResolvedTheme = useMemo(
    () => (theme === "system" ? systemTheme : theme),
    [theme, systemTheme]
  );

  useEffect(() => {
    applyThemeClass(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback(
    (next: ThemeMode) => {
      const previous = theme;
      userHasChosenRef.current = true;
      writeStoredTheme(next);
      setThemeState(next);

      // Persist to the user record. On failure, revert local state and cache.
      updateUserTheme(next).catch((err) => {
        console.error("Failed to persist theme preference:", err);
        writeStoredTheme(previous);
        setThemeState(previous);
      });
    },
    [theme]
  );

  const applyHostHint = useCallback((resolved: ResolvedTheme) => {
    if (userHasChosenRef.current) return;
    // Hint maps to an explicit light/dark, but we don't persist it —
    // the user keeps "system" semantics and can still drift with their OS later.
    setThemeState(resolved);
  }, []);

  const hydrateFromServer = useCallback((serverTheme: ThemeMode) => {
    if (!isThemeMode(serverTheme)) return;
    userHasChosenRef.current = true;
    writeStoredTheme(serverTheme);
    setThemeState(serverTheme);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, applyHostHint, hydrateFromServer }),
    [theme, resolvedTheme, setTheme, applyHostHint, hydrateFromServer]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
};
